import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LinkImport } from "@/components/admin/LinkImport";
import { CheckCircle2, CircleSlash, Files, Loader2, UploadCloud, XCircle } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useSpace } from "@/context/SpaceContext";
import { sendChunks, startUpload } from "@/lib/chunkedUpload";
import { resetAtelier } from "@/lib/atelierChoice";
import { atelierSentText } from "@/components/atelier/AtelierSent";
import { FilePick } from "@/components/admin/FilePick";
import { DriveImportButton } from "@/components/admin/DriveImportButton";
import { PublishProgress } from "@/components/admin/PublishProgress";

const ICON = { queued: Files, uploading: Loader2, done: CheckCircle2, dup: CircleSlash, error: XCircle };
const TONE = { queued: "text-neutral-400", uploading: "text-[var(--gold-bright)]", done: "text-emerald-300", dup: "text-amber-300", error: "text-red-300" };

const resultText = (t, res) => (res.atelier ? atelierSentText(t, res.version.version, res.version.version_code) :
  (res.result === "new_app" ? t("bulk.newApp", { name: res.app.name }) : t("bulk.newVersion", { name: res.app.name, v: res.version.label }))) + (res.dedup ? ` · ${t("upload.dedup")}` : "");

// Several APKs at once: sequential chunked uploads (one at a time server-side), matched to apps by package name.
export const BulkUploader = ({ onDone }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const patch = (i, p) => setItems((list) => list.map((it, j) => (j === i ? { ...it, ...p } : it)));

  const add = (files) => {
    const apks = files.filter((f) => f.name.toLowerCase().endsWith(".apk"));
    const bad = files.filter((f) => !f.name.toLowerCase().endsWith(".apk"));
    setItems((list) => [
      ...list.filter((it) => it.status !== "queued" || !running),
      ...apks.map((file) => ({ file, status: "queued", progress: 0, msg: "" })),
      ...bad.map((file) => ({ file, status: "error", progress: 0, msg: t("err.apkOnly") })),
    ]);
    if (input.current) input.current.value = "";
  };

  const run = async () => {
    setRunning(true);
    for (let i = 0; i < items.length; i += 1) {
      const { file, status } = items[i];
      if (status !== "queued") continue;
      patch(i, { status: "uploading" });
      try {
        const init = await startUpload({ base, file });
        const res = await sendChunks({ base, uploadId: init.upload_id, file, chunkSize: init.chunk_size, totalChunks: init.total_chunks,
          onProgress: (p) => patch(i, { progress: p }) });
        patch(i, { status: "done", progress: 100, msg: resultText(t, res), appId: res.app?.id || null, atelierId: res.atelier ? res.version.id : null });
      } catch (err) {
        const dup = err?.response?.status === 409 && /déjà en ligne/.test(err?.response?.data?.detail || "");
        patch(i, { status: dup ? "dup" : "error", msg: dup ? t("bulk.dup") : `${t("bulk.error")} — ${translateError(err, t)}` });
      }
    }
    setRunning(false);
    resetAtelier();
    onDone?.();
  };

  const queued = items.filter((it) => it.status === "queued").length;
  return (
    <section
      className={`card-carbon rounded-2xl p-5 transition-colors duration-200 ${over ? "ring-2 ring-[var(--gold)]" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!running) add(Array.from(e.dataTransfer.files || [])); }}
      data-testid="bulk-uploader"
    >
      <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><UploadCloud className="h-5 w-5 text-[var(--gold)]" /> {t("bulk.title")}</h3>
      <p className="mt-1 text-xs text-neutral-400">{t("bulk.hint")}</p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <FilePick id="bulk-files" multiple accept=".apk,application/vnd.android.package-archive" disabled={running} inputRef={input}
            files={null} onFiles={add} testId="bulk-file-input" label={t("file.chooseMany")} />
        </div>
        <button type="button" className="btn-gold btn-sm btn-cta" disabled={running || !queued} onClick={run} data-testid="bulk-start-btn">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {t("bulk.start")} {queued ? `(${queued})` : ""}
        </button>
      </div>
      <div className="mt-3"><DriveImportButton onDone={onDone} /></div>
      <LinkImport onDone={onDone} />
      {items.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="bulk-list">
          {items.map((it, i) => {
            const Icon = ICON[it.status];
            return (
              <li key={`${it.file.name}-${i}`} className="rounded-xl border border-neutral-800 bg-black/40 p-3" data-testid={`bulk-item-${i}`}>
                <div className="flex min-w-0 items-center gap-2 text-xs">
                  <Icon className={`h-4 w-4 shrink-0 ${TONE[it.status]} ${it.status === "uploading" ? "animate-spin" : ""}`} />
                  <span className="min-w-0 flex-1 truncate text-neutral-200" title={it.file.name}>{it.file.name}</span>
                  {it.status === "uploading" && <span className="font-display font-bold text-[var(--gold-bright)]">{Math.floor(it.progress)}%</span>}
                </div>
                {it.status === "uploading" && (
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/60"><div className="progress-fill h-full rounded-full" style={{ width: `${it.progress}%` }} /></div>
                )}
                <p className={`mt-1 break-words text-xs ${TONE[it.status]}`} data-testid={`bulk-item-result-${i}`}>{it.status === "queued" ? t("bulk.queued") : it.msg}</p>
                {it.atelierId && <Link to={`/admin/atelier?item=${it.atelierId}`} className="text-xs font-semibold text-amber-200 underline" data-testid={`bulk-item-atelier-${i}`}>{t("atelier.openItem")}</Link>}
                {it.appId && <PublishProgress appId={it.appId} testId={`bulk-item-publish-${i}`} />}
              </li>
            );
          })}
        </ul>
      )}
      {items.length > 0 && !running && (
        <button type="button" className="mt-3 text-xs text-neutral-400 underline hover:text-white" onClick={() => setItems([])} data-testid="bulk-clear-btn">{t("bulk.clear")}</button>
      )}
    </section>
  );
};
